<script setup lang="ts">
import { format } from "date-fns/format";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import {
  getUserFriendlyName,
  MediaType,
  type EstimatorReportComparisonDto,
  type EstimatorReportDto,
} from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import AddToPresentationMenu from "@/views/AddToPresentationMenu.vue";
import EstimatorReportComparison from "@/views/comparison/EstimatorReportComparison.vue";

const api = useApi();
const route = useRoute();
const router = useRouter();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);
const comparisonId = computed(() => parseInt(route.params.comparisonId as string));

const isLoading = ref(true);
const comparison = ref<EstimatorReportComparisonDto>();
const reports = ref<EstimatorReportDto[]>([]);
const selectedModelsIds = ref<number[]>([]);
const tradeoffExplanations = ref<{ reportId: string; explanation: string }[]>([]);
const creatorName = ref("");

const breadcrumb = computed(() => {
  const last = comparison.value?.name ?? "Comparison";
  return [
    {
      label: tenantName.value,
      to: { name: RouteNames.Projects, params: { tenantName: tenantName.value } },
      icon: "icon-masonry",
    },
    {
      label: projectName.value,
      to: {
        name: RouteNames.ProjectDetail,
        params: { tenantName: tenantName.value, projectName: projectName.value },
      },
      icon: "icon-punch-card",
      iconColor: "var(--color-background-purple)",
    },
    {
      label: last,
      icon: "icon-scale",
    },
  ];
});

async function loadComparison() {
  isLoading.value = true;

  const c = await api.fetchComparison(tenantName.value, projectName.value, comparisonId.value);
  if (c === null) {
    router.replace({ name: RouteNames.NotFound });
  } else {
    comparison.value = c;

    // fetch reports and filter potentially not found
    const results = (
      await Promise.all(
        c.compared_reports.map((id) =>
          api.fetchEstimatorReport(tenantName.value, projectName.value, id)
        )
      )
    ).filter((r) => r !== null);
    reports.value = results;

    // populate selected trade offs
    selectedModelsIds.value = c.best_trade_offs;
    tradeoffExplanations.value = Object.entries(c.best_trade_offs_explanations).map(
      ([reportId, explanation]) => ({
        reportId,
        explanation,
      })
    );

    // get creator info
    const user = await api.fetchUser(c.creator_id);
    if (user) {
      creatorName.value = getUserFriendlyName(user);
    }
  }

  // render
  isLoading.value = false;
}

function formatDatetime(dt?: string) {
  if (dt) {
    return format(new Date(dt), "d MMMM yyyy hh:mm");
  }
  return "";
}

onMounted(async () => {
  await loadComparison();
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="breadcrumb">
    <template #default>
      <div class="comparison-view">
        <div class="header">
          <div>
            <div class="back label-s" @click="router.back()">
              <i class="icon icon-arrow-left" /> Comparisons
            </div>
            <div class="title">
              <h6><i class="icon icon-scale" />{{ comparison?.name }}</h6>
              <AddToPresentationMenu
                v-if="comparison !== undefined"
                :name="`Comparison ${comparison.name}`"
                :comparison_id="comparison.id"
                :media_type="MediaType.Comparison"
                :value="JSON.stringify({ id: comparison.id })"
              />
            </div>
          </div>
          <div class="info">
            <div>
              <div class="name">Created at:</div>
              <div class="value">{{ formatDatetime(comparison?.created_at) }}</div>
            </div>
            <div>
              <div class="name">Creator:</div>
              <div class="value">
                <UserAvatar :name="creatorName" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>
        <div v-else class="comparison">
          <EstimatorReportComparison
            :reports="reports"
            :is-read-only="true"
            v-model:tradeoff-explanations="tradeoffExplanations"
            v-bind:selected-models-ids="selectedModelsIds"
          />
        </div>
      </div>
    </template>
  </DefaultPageLayout>
</template>

<style scoped>
.loading {
  display: flex;
  height: var(--page-remaining-height);
  align-items: center;
  justify-content: center;
}

.comparison-view {
  display: flex;
  height: var(--page-remaining-height);
  flex-direction: column;
  gap: var(--spacing-16);

  .back {
    color: var(--color-text-secondary);
    cursor: pointer;

    .icon {
      margin-right: var(--spacing-8);
      vertical-align: middle;
    }
  }

  .loading {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  .comparison {
    overflow: hidden;
    height: 100%;
    flex-grow: 1;
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .title {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-6);

      h6 {
        .icon {
          margin-right: var(--spacing-6);
          color: var(--color-icon-tertiary);
          vertical-align: middle;
        }
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8);

      & > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-12);

        .name {
          color: var(--color-text-secondary);
          font-weight: var(--font-weight-medium);
        }

        &.copy-id {
          cursor: pointer;

          i {
            padding: var(--spacing-4);
            border: solid var(--color-stroke-background-primary) var(--stroke-width-m);
            border-radius: var(--radius-xs);
            vertical-align: middle;
          }
        }
      }
    }
  }
}
</style>
